import {FaSolidCheck, FaSolidCircleExclamation, FaSolidTriangleExclamation} from "solid-icons/fa";
import {JSX, JSXElement, Show, splitProps} from "solid-js";
import {type FormResponse} from "@modular-forms/solid"
import css from "./form.module.css";
import PasswordInput from "../../ui/PasswordInput";
import Checkbox from "~/components/ui/Checkbox";
import {CheckedChangeDetails} from "@zag-js/checkbox";

export interface FieldProps extends InputProps {
    children: (props: InputProps) => JSXElement;
    label?: string;
    error?: string;
}

export interface InputProps {
    name: string;
    type: 'text' | 'email' | 'tel' | 'password' | 'url' | 'date' | 'checkbox';
    placeholder?: string;
    value: string | undefined;
    required?: boolean;
    disabled?: boolean;
    hidden?: boolean;
    error?: string;
    ref: (element: HTMLInputElement) => void;
    onInput?: JSX.EventHandler<HTMLInputElement, InputEvent>;
    onChange?: JSX.EventHandler<HTMLInputElement, Event>;
    onBlur?: JSX.EventHandler<HTMLInputElement, FocusEvent>;
}

export function FormContainer(props: { children: JSXElement }) {
    return <div class={css.formContainer}>
        {props.children}
    </div>
}

export function Field(props: FieldProps) {

    const [, inputProps] = splitProps(props, ["children", "label"]);

    return (
        <div class={css.field}>
            {props.label && (
                <label for={props.name} class={css.label}>
                    {props.label}{props.required && <span class={css.required}>*</span>}
                </label>
            )}

            <props.children {...inputProps}/>

            {props.error && (
                <p id={`${props.name}-error`} class={css.error} role="alert">
                    <FaSolidCircleExclamation class={css.errorIndicator}/> {props.error}
                </p>
            )}
        </div>
    )
}

export function InputField(props: Omit<FieldProps, 'children'>) {

    return (
        <Field {...props}>
            {(inputProps) => (
                <Input {...inputProps}/>
            )}
        </Field>
    )
}

export function Input(props: InputProps) {
    return (
        <input class={css.input} {...props} id={props.name} value={props.value || ''}
               aria-invalid={!!props.error}
               aria-errormessage={`${props.name}-error`}/>
    )
}

export function PasswordInputField(props: Omit<FieldProps, 'children' | 'type'>) {


    return (
        <Field {...props} type="password">
            {(inputProps) => (
                <PasswordInput
                    input={(props: any) =>
                        <Input {...inputProps} {...props}/>
                    }
                />
            )}
        </Field>
    )
}

export interface CheckboxInputFieldProps extends Omit<FieldProps, 'children' | 'type' | 'label' | 'onInput' | 'onChange' | 'onBlur'> {
    onCheckedChange: (checked: CheckedChangeDetails) => void;
    checked: boolean;
    label?: JSX.Element
}

export function CheckboxInputField(props: CheckboxInputFieldProps) {

    const [checkboxProps, fieldProps] = splitProps(props, ["label", "checked", "onCheckedChange"])

    return (
        <Field {...fieldProps} type="checkbox">
            {(inputProps) => (
                <Checkbox
                    input={(props: any) =>
                        <input {...inputProps} {...props}/>
                    }
                    {...checkboxProps}
                />
            )}
        </Field>
    )
}

function RadioField(props: Omit<FieldProps, 'children'>) {
    return (
        <Field {...props}>
            {(inputProps) => (
                <input {...inputProps} id={inputProps.name} type="radio" value={inputProps.value || ''}
                       aria-invalid={!!props.error} aria-errormessage={`${inputProps.name}-error`}/>
            )}
        </Field>
    )
}

export function FieldGroup(props: { children: JSXElement, title?: string }) {
    return (
        <div class={css.fieldGroup}>
            <Show when={props.title}>
                <h2 class={css.fieldGroupTitle}>{props.title}</h2>
            </Show>
            {props.children}
            <hr/>
        </div>
    )
}

export function ButtonGroup(props: { children: JSXElement }) {
    return (
        <div class={css.buttonGroup}>
            {props.children}
        </div>
    )
}

export function FormResponse(props: { response: FormResponse<any> }) {

    const className = () => {
        switch (props.response.status) {
            case "error":
                return css.responseError
            case "success":
                return css.responseSuccess
            case "info":
                return css.responseInfo
        }
    }

    const icon = () => {
        switch (props.response.status) {
            case "error":
                return <FaSolidTriangleExclamation/>
            case "success":
                return <FaSolidCheck/>
        }
    }

    return (
        <Show when={props.response.message}>
            <div class={className() ?? ""}>
                <Show when={icon()} keyed>
                    {icon => icon}
                </Show>
                <span>{props.response.message}</span>
            </div>
        </Show>
    )
}